import React, { useMemo } from 'react';

import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Link } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { ContentTypeEnum } from 'constants-content';
import { useMyRecentActivityQuery } from 'react-hooks';

import { Loading } from 'components/general';
import { UserContentInteractionCard } from 'components/lane';

import RecentActivityFilters from './RecentActivityFilters';
import { useRecentActivityFilters } from './useRecentActivityFilters';

import styles from './RecentActivity.scss';

export function RecentActivity() {
  const { filters, updateFilters } = useRecentActivityFilters();
  
  // Convert filters to match the expected type for useMyRecentActivityQuery
  const queryFilters = useMemo(() => ({
    search: filters.search || undefined,
    sortBy: filters.sortBy || undefined,
    startDate: filters.startDate,
    endDate: filters.endDate,
  }), [filters.search, filters.sortBy, filters.startDate, filters.endDate]);
  
  // Use real data fetching hook
  const { items: interactions, loading, getNextPage, pageInfo } = useMyRecentActivityQuery(queryFilters);
  
  const hasNextPage = interactions.length < pageInfo.total;
  
  const debouncedGetNextPage = useDebouncedCallback(getNextPage, 500).callback;

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: debouncedGetNextPage,
    rootMargin: '0px 0px 100px 0px',
  });

  const redirectionPath = (interaction: any) => {
    return interaction.contentData.type === ContentTypeEnum.WorkOrder
      ? `/l/post/${interaction.contentData._id}?uciId=${interaction._id}`
      : `/l/interaction/${interaction._id}`;
  };

  return (
    <div className={styles.RecentActivity} data-test="recent-activity-container">
      <RecentActivityFilters filters={filters} onFiltersChange={updateFilters} />
      
      {interactions
        .filter(
          interaction =>
            interaction.contentData.type !== ContentTypeEnum.WorkOrder
        )
        .map(interaction => (
          <Link
            to={redirectionPath(interaction)}
            key={interaction._id}
            className={styles.Item}
          >
            <UserContentInteractionCard
              className={styles.interactionCard}
              interaction={interaction}
            />
          </Link>
        ))}

      {loading && <Loading />}
      <div ref={sentryRef} />
    </div>
  );
}
